import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  LoadingOverlay,
  Stack,
  Tabs,
  Text,
  Title,
} from "@mantine/core";
import { useSearchParams } from "react-router-dom";
import { useApp } from "../../context/AppContext";
import useLoyaltyProgram from "../../hooks/useLoyaltyProgram";
import LoyaltyOnboardingWizard from "./LoyaltyOnboardingWizard";
import SummaryTab from "./SummaryTab";
import SettingsTab from "./SettingsTab";
import {
  LoyaltySettings,
  LoyaltyEarnRule,
  LoyaltyReward,
} from "../../types/loyalty";

/**
 * Loyalty page (loyalty FE1 §1/§3). Keyed on the currently selected business
 * location. Two states, based on whether GET loyalty/settings returns an
 * existing configured row:
 *  - not configured → empty-state landing + "Set up my program" → wizard (§2)
 *  - configured → Overview / Promotions / Tiers / Settings tab view (§3)
 */
export default function RewardsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { getLoyaltyEarnRules, getLoyaltyRewards } = useApp();
  const { businessLocationId, settings, isConfigured, isLoading, refresh } =
    useLoyaltyProgram();

  const [wizardOpen, setWizardOpen] = useState(false);
  const [earnRules, setEarnRules] = useState<LoyaltyEarnRule[]>([]);
  const [rewards, setRewards] = useState<LoyaltyReward[]>([]);
  const [configLoading, setConfigLoading] = useState(false);

  const loadConfig = useCallback(async () => {
    if (!businessLocationId) return;
    setConfigLoading(true);
    try {
      const [rules, rewardList] = await Promise.all([
        getLoyaltyEarnRules(businessLocationId),
        getLoyaltyRewards(businessLocationId),
      ]);
      setEarnRules(rules);
      setRewards(rewardList);
    } catch (error) {
      // Backend PR2 not deployed yet — keep the page functional with empty
      // config rather than crashing.
      console.error("Error loading loyalty config:", error);
      setEarnRules([]);
      setRewards([]);
    } finally {
      setConfigLoading(false);
    }
  }, [businessLocationId, getLoyaltyEarnRules, getLoyaltyRewards]);

  useEffect(() => {
    if (isConfigured) {
      loadConfig();
    }
  }, [isConfigured, loadConfig]);

  // ?setup=1 (onboarding card) opens the wizard directly when the program
  // isn't configured yet; consumed once so back/refresh behaves normally.
  useEffect(() => {
    if (searchParams.get("setup") && !isLoading) {
      if (!isConfigured) {
        setWizardOpen(true);
      }
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, isLoading, isConfigured, setSearchParams]);

  const refreshAll = useCallback(async () => {
    await refresh();
    await loadConfig();
  }, [refresh, loadConfig]);

  // Normalize the configured row so downstream tabs get full defaults even
  // if the backend omits optional fields.
  const normalizedSettings = useMemo<LoyaltySettings | null>(() => {
    if (!isConfigured || !settings) return null;
    return {
      ...settings,
      enabled: settings.enabled === true,
      preset: settings.preset ?? "both",
      deduct_redemption_from_commission:
        settings.deduct_redemption_from_commission === true,
      points_expiration_policy: settings.points_expiration_policy ?? "none",
      points_expiration_days: settings.points_expiration_days ?? null,
    };
  }, [isConfigured, settings]);

  if (isLoading && !normalizedSettings) {
    return (
      <Stack p="md" mih="60dvh" pos="relative">
        <LoadingOverlay visible />
      </Stack>
    );
  }

  return (
    <Stack p="md" gap="md" mih="100dvh" pos="relative">
      {/* Loyalty reporting lives inside the Reports page (§5) — no header
          button here. */}
      <Title order={2}>Loyalty</Title>

      {!normalizedSettings ? (
        <Stack align="center" justify="center" gap="md" mih="55dvh">
          <Title order={1} ta="center">
            Let&apos;s get your loyalty program started!
          </Title>
          <Text size="sm" c="dimmed" ta="center" maw={460}>
            Reward your clients for visits, purchases, and referrals. Setting up
            a loyalty program is quick and easy, you can always adjust it later.
          </Text>
          <Button
            color="var(--brand-teal)"
            size="md"
            onClick={() => setWizardOpen(true)}
          >
            Set up my program
          </Button>
        </Stack>
      ) : (
        <Tabs defaultValue="summary" keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="summary">Summary</Tabs.Tab>
            <Tabs.Tab value="settings">Settings</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="summary" pt="lg">
            {businessLocationId && (
              <SummaryTab
                businessLocationId={businessLocationId}
                launchedDt={normalizedSettings.created_dt ?? null}
              />
            )}
          </Tabs.Panel>
          <Tabs.Panel value="settings" pt="lg">
            <Stack pos="relative">
              <LoadingOverlay visible={configLoading} />
              {businessLocationId && (
                <SettingsTab
                  businessLocationId={businessLocationId}
                  settings={normalizedSettings}
                  earnRules={earnRules}
                  rewards={rewards}
                  onRefresh={refreshAll}
                />
              )}
            </Stack>
          </Tabs.Panel>
        </Tabs>
      )}

      {businessLocationId && (
        <LoyaltyOnboardingWizard
          opened={wizardOpen}
          businessLocationId={businessLocationId}
          onClose={() => setWizardOpen(false)}
          onPublished={() => {
            setWizardOpen(false);
            refreshAll();
          }}
        />
      )}
    </Stack>
  );
}
